import { Color, Spacing } from '@persona-id/design-tokens';
import { groupBy, lowerCase, startCase, upperFirst } from 'lodash/fp';
import { Link } from 'react-router-dom-v5-compat';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import Pii from 'dashboard/components/Pii';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { DocumentGeneric, MatchLevel } from 'dashboard/models/document';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import Sticker from 'design-system/components/Sticker';
import { FontWeight } from 'design-system/variables/text';
import { ApiModel } from 'lib/interfaces/api';
import { DocumentConfig, DocumentConfigDocumentsUpload } from 'lib/interfaces/documentConfig';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { UiStepType } from 'lib/interfaces/StepsConfig';

export const StyledAccordion = styled(Accordion)`
  width: 100%;

  > *:first-child {
    padding-left: ${Spacing[4]};
  }
`;

const DocumentInfoContainer = styled('div')`
  margin-top: ${Spacing[4]};
  margin-bottom: ${Spacing[5]};
  display: grid;
  grid-template-columns: min-content auto;
  grid-column-gap: ${Spacing[4]};
`;

interface DocumentThumbnailProps {
  document: DocumentGeneric;
  featureFlags: FeatureFlagsMap;
}
const DocumentThumbnail = ({ document }: DocumentThumbnailProps) => {
  const { files, 'files-normalized': filesNormalized } = document.attributes;
  // if the document has not been processed it does not have normalized files
  // we default to using normalized files since some image formats that are not compatible
  // with browsers (HEIC images) have been converted to images
  const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;
  const documentViewerLink = `/dashboard/document-viewer/${document.id}`;

  return (
    <div
      id={`inquiry-documents__file-thumbnail--${document?.id || 'no-document'}`}
      data-test={`inquiry-documents__file-thumbnail--${document?.id || 'no-document'}`}
    >
      <Link to={documentViewerLink} state={{ internalRedirect: true }}>
        <AsyncImage
          value={displayFiles.map((file) => ({
            url: file.url,
            label: file.filename,
            name: file.filename,
            type: file['file-type'],
          }))}
          showCount
          showExtension
          width='100px'
          height='100px'
          allowPreviewZoom={false}
        />
      </Link>
    </div>
  );
};

interface ExtractionInfo {
  fieldName: string;
  result: string;
  matchLevel: MatchLevel;
  count: number;
}

interface InquiryDocumentsProps {
  documents: DocumentGeneric[];
  template: InquiryTemplate | Template;
  isRedacted: boolean;
  featureFlags: FeatureFlagsMap;
}

const InquiryDocuments = ({
  featureFlags,
  documents,
  template,
  isRedacted,
}: InquiryDocumentsProps) => {
  // Only display if a document has been started.
  if (documents.length === 0) {
    return null;
  }

  // TODO: [PER-6356] handle inquiry template versions once Dynamic Flow supports Doc Upload
  const documentsRequiredFromTemplate: DocumentConfig[] =
    template.type === ApiModel.Template ? documentsRequired(template) : [];

  return (
    <div>
      {documents.map((document) => {
        let matchCount = 0;
        const uniqueExtractions: ExtractionInfo[] =
          document.attributes['extraction-responses']?.flatMap((extraction) =>
            Object.entries(groupBy((x) => x.value.toLowerCase(), extraction.results)).map(
              ([_, items]) => {
                matchCount += items.length;
                return {
                  fieldName: extraction['field-name'],
                  result: items[0]['value'],
                  matchLevel: items[0]['match-level'],
                  count: items.length,
                };
              },
            ),
          ) || [];

        return (
          <Accordion
            key={document.id}
            dataTest={`inquiry-documents__document-row--${document?.id || 'no-document'}`}
            title={
              <Title
                document={document}
                isOptional={documentIsOptional(documentsRequiredFromTemplate, document)}
                isRedacted={isRedacted}
                matchCount={matchCount}
              />
            }
            menuItemBorder={false}
            menuItemHover={false}
            padded={false}
            disabled={isRedacted}
            disabledTheme='primary'
            disabledHideCaret
            menuItem
          >
            <DocumentInfoContainer data-test='inquiry-documents__document-info'>
              <Pii isRedacted={false /* AsyncImage has its own empty state */}>
                <DocumentThumbnail document={document} featureFlags={featureFlags} />
              </Pii>
              {matchCount > 0 ? (
                <AttributeGrid colCount={2}>
                  {uniqueExtractions.map(({ fieldName, result, matchLevel }, index) => (
                    <AttributeItem
                      key={`${fieldName}-${index}`}
                      title={`${startCase(fieldName)} (${matchLevel} match)`}
                      value={result}
                    />
                  ))}
                </AttributeGrid>
              ) : (
                <NoDocumentText>No fields extracted</NoDocumentText>
              )}
            </DocumentInfoContainer>
          </Accordion>
        );
      })}
    </div>
  );
};

export default InquiryDocuments;

interface TitleProps {
  document: DocumentGeneric;
  isOptional: boolean;
  isRedacted: boolean;
  matchCount: number;
}

const Title = ({ document, isOptional, isRedacted, matchCount }: TitleProps) => {
  const matchText = matchCount === 1 ? `${matchCount} match` : `${matchCount} matches`;
  return (
    <StyledTitleContainer data-test='inquiry-documents__document-title'>
      <span>
        &lsquo;Document AI - &rsquo;{upperFirst(lowerCase(document.attributes.kind))}
        {isOptional && ' (Optional)'}
      </span>
      <TitleStickerContainer>
        <Pii isRedacted={isRedacted}>
          <Sticker variant='basic'>{matchText}</Sticker>
        </Pii>
      </TitleStickerContainer>
    </StyledTitleContainer>
  );
};

const StyledTitleContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: max-content auto;
  line-height: 2.5;
  font-weight: ${FontWeight.Medium};
  border-bottom: 1px solid ${Color.CoreSlate50};
  grid-column: 1/-1;
`;

const TitleStickerContainer = styled('div')`
  align-items: center;
`;

const NoDocumentText = styled('div')`
  color: ${COLORS.gray50};
`;

const documentsRequired = (template: Template): DocumentConfig[] =>
  template.attributes['steps-config'].steps
    .filter((step) => step.type === UiStepType.DocumentsUpload)
    .map(
      (documentsUploadStep) =>
        (documentsUploadStep.config as DocumentConfigDocumentsUpload).required,
    )
    .flat();

const documentIsOptional = (
  requiredDocuments: DocumentConfig[],
  document: DocumentGeneric,
): boolean => {
  // TODO: Template can be updated to remove document after document was submitted. Assume document
  // is not optional in this case. Long term fix is to store config on document when it's created.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return requiredDocuments.find((r) => {
    return r.kind === document.attributes.kind;
  })?.optional;
};
